import { forwardRef } from "react";
import { Link, LinkProps, useParams } from "react-router-dom";
import { validLanguages } from "@/lib/seo";

interface LocalizedLinkProps extends Omit<LinkProps, "to"> {
  to: string;
}

/**
 * A Link component that automatically adds the current language prefix to paths.
 * English links have NO prefix (served from root).
 * Other languages get /:lang/ prefix.
 */
export const LocalizedLink = forwardRef<HTMLAnchorElement, LocalizedLinkProps>(
  ({ to, children, ...props }, ref) => {
    const { lang } = useParams<{ lang: string }>();

    // Determine current language: from URL param or default to English
    const currentLang = lang && validLanguages.has(lang) ? lang : "en";

    // If it's an external link, use as-is
    if (to.startsWith("http") || to.startsWith("mailto:") || to.startsWith("tel:")) {
      return (
        <Link ref={ref} to={to} {...props}>
          {children}
        </Link>
      );
    }

    // Check if path already has language prefix
    const firstSegment = to.split("/")[1];
    if (validLanguages.has(firstSegment)) {
      // If link already has /en/, redirect to root equivalent
      if (firstSegment === "en") {
        const pathWithoutEn = to.replace(/^\/en\/?/, "/") || "/";
        return (
          <Link ref={ref} to={pathWithoutEn} {...props}>
            {children}
          </Link>
        );
      }
      return (
        <Link ref={ref} to={to} {...props}>
          {children}
        </Link>
      );
    }

    // For English: no prefix (root)
    if (currentLang === "en") {
      return (
        <Link ref={ref} to={to} {...props}>
          {children}
        </Link>
      );
    }

    // For other languages: add prefix
    const localizedPath = `/${currentLang}${to === "/" ? "" : to}`;

    return (
      <Link ref={ref} to={localizedPath} {...props}>
        {children}
      </Link>
    );
  }
);

LocalizedLink.displayName = "LocalizedLink";
